import { createElement, FC, Fragment, useContext, useEffect } from 'react';
import { Redirect } from 'react-router';
import { useMount } from 'react-use';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import ProjectEditingStep from '../../../../entity/ProjectEditingStep';
import { isFlatProjectType } from '../../../../utility/isFlatProjectType';
import { context as DependencyContext } from '../../../context/dependency';
import { context as StateContext } from '../../../context/state';
import { isProjectFlatAtom } from '../atoms/isProjectFlatAtom';
import { projectAtom } from '../atoms/projectAtom';
import { workflowStepsAtom } from '../atoms/workflowStepsAtom';
import { getOrganization, getWorkflowSteps } from '../domain/helperFunctions';
import { useEditingProjectState } from '../hooks/useEditingProjectState';
import { useGetProject } from '../hooks/useGetProject';
import {
  BasicSettingScreen,
  CreativeBriefScreen,
  DueScreen,
  EditSubmittedScreen,
  OptionScreen,
  PreviewScreen,
  SubmitCompletionScreen,
} from '../index';
import { projectHasOptionsSelector } from '../selectors/projectSelectors';

type Props = {
  projectId: string;
  step: ProjectEditingStep;
  setConfirmAlert: (state: boolean) => void;
};

const ScreenByStep: FC<Props> = ({ projectId, step, setConfirmAlert }) => {
  const { initializeEditingProject } = useEditingProjectState();
  const { lastAccessedOrganization, availableOrganizations } = useContext(StateContext);
  const { authenticationUsecase } = useContext(DependencyContext);
  const [isFlat, setIsFlat] = useRecoilState(isProjectFlatAtom);
  const setWorkflowSteps = useSetRecoilState(workflowStepsAtom);
  const hasOption = useRecoilValue(projectHasOptionsSelector);
  const project = useRecoilValue(projectAtom);
  const organization = getOrganization(lastAccessedOrganization, availableOrganizations);

  const { getProject } = useGetProject();

  useMount(async () => {
    authenticationUsecase.needSignedIn();
    await getProject({ projectId, step });
  });

  // We're going to use Apollo Client's cache as the true value of the project for read operations.
  // For local writes from fields and inputs, we'll use inputted state, where we convert raw graphQL response
  // data into something the frontend can easily handle
  useEffect(() => {
    project && initializeEditingProject(project);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project]);

  useEffect(() => {
    if (organization && project?.projectType.key) {
      setIsFlat(isFlatProjectType(`${project?.projectType.key}`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, project?.projectType.key, isFlatProjectType]);

  useEffect(() => {
    setWorkflowSteps(getWorkflowSteps(hasOption, isFlat));
  }, [setWorkflowSteps, hasOption, isFlat]);

  // TODO: Apollo Clientの使い方を考えれば、ペライチ実装を別のページとして扱い、isFlatなどを考慮しなくても良いようにするのが一番。
  //   シンプルな実装で、設計も非常に簡単になるはず。
  if (isFlat && (step === undefined || step === 'format' || step === 'due' || step === 'brief')) {
    return (
      <>
        <BasicSettingScreen setConfirmAlert={setConfirmAlert} />
        <DueScreen />
        <CreativeBriefScreen setConfirmAlert={setConfirmAlert} />
      </>
    );
  }

  if (step === undefined || step === 'format') return <BasicSettingScreen setConfirmAlert={setConfirmAlert} />;
  if (step === 'due') return <DueScreen />;
  if (step === 'brief') return <CreativeBriefScreen setConfirmAlert={setConfirmAlert} />;
  if (step === 'option') return <OptionScreen />;
  if (step === 'preview') return <PreviewScreen setConfirmAlert={setConfirmAlert} />;

  // SubmitCompletionScreen(発注完了画面)とEditSubmittedScreen(指示書編集画面)はプロジェクト発注後でないとアクセスできないので、
  // Draft状態のプロジェクトでアクセスしようとすると`/edit`にリダイレクトされるような挙動にする
  if (step === 'complete' && project?.state !== 'draft') return <SubmitCompletionScreen />;
  if (step === 'submitted' && project?.state !== 'draft')
    return <EditSubmittedScreen setConfirmAlert={setConfirmAlert} />;

  return <Redirect to={`/projects/${projectId}/edit`} />;
};

export default ScreenByStep;
